<script lang="ts">
  import Button from '../../common/buttons/Button.svelte';
  import Modal from '../SmallModal/Modal.svelte';
  import { createEventDispatcher, onMount } from 'svelte';
  import KeyboardHint from '$icons/KeyboardHintIcon.svelte';
  import FolderEmpty from '$icons/PostFolderIcon.svelte';
  import AddSquare from '$icons/AddSquareIcon.svelte';
  import {
    addItemsToGalleries,
    addPostToGalleries,
    getMyGalleries,
    type MyGalleriesSingleResponse
  } from '$src/utils/api/gallery.util';
  import { fade, fly, slide } from 'svelte/transition';
  import Loader from '$icons/LoaderIcon.svelte';
  import GalleryCard from '$lib/components/gallery/GalleryCard.svelte';
  import { parseErrorCode } from 'garden-common/index';
  import { isMobile } from '$src/stores/media.store';

  export let postId: string;
  export let type: 'post' | 'talent' = 'post';

  const dispatch = createEventDispatcher();

  let nGalleriesSelected = 0;
  $: nGalleriesSelected = galleries.filter(e => e.selected).length;

  function closeModal() {
    dispatch('ModalClose');
  }

  function openCreateGallery() {
    dispatch('OpenCreateGallery');
  }

  let isFetchingGalleries = false;
  let galleries: (MyGalleriesSingleResponse & { selected?: boolean })[] = [];

  async function fetchUserGalleries() {
    isFetchingGalleries = true;

    const res = await getMyGalleries(true);
    galleries = res;

    isFetchingGalleries = false;
  }

  let error = '';
  let success = '';
  let isAddingToGalleries = false;

  async function addToGalleries() {
    isAddingToGalleries = true;

    if (type === 'post') {
      await addPostToGalleries(
        postId,
        galleries.filter(g => g.selected).map(g => g._id)
      ).catch(err => {
        switch (parseErrorCode(err.response.data.gardenCode)) {
          case 'ALREADY_EXISTS':
            error = 'This post is already in the gallery';
            break;
          default:
            error = err.response.data.message;
            break;
        }

        const simplebar = document.getElementsByClassName('simplebar-content-wrapper')?.[0];
        simplebar.scrollTo({ top: 0, behavior: 'smooth' });
      });
    } else if (type === 'talent') {
      await addItemsToGalleries(
        [
          {
            type: 'talent',
            _id: postId
          }
        ],
        galleries.filter(g => g.selected).map(g => g._id)
      ).catch(err => {
        switch (parseErrorCode(err.response.data.gardenCode)) {
          case 'ALREADY_EXISTS':
            error = 'This post is already added to the gallery';
            break;
          default:
            error = err.response.data.message;
            break;
        }

        const simplebar = document.getElementsByClassName('simplebar-content-wrapper')?.[0];
        simplebar.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }

    isAddingToGalleries = false;
    if (error) return;

    dispatch('Saved');
    success = `Saved ${type} to gallery`;
    setTimeout(closeModal, 3000);
  }

  onMount(fetchUserGalleries);
</script>

<Modal contentOverflow class="h-[800px]" on:ModalClose={closeModal}>
  <!-- Header -->
  <div slot="header" class="flex flex-col items-center gap-8 mt-12 mb-6">
    <FolderEmpty class="stroke-brand-green" size={'64'} />

    <div class="flex flex-col items-center gap-2">
      <h1 class="text-2xl font-bold">Save to your gallery</h1>

      <h2 class="text-grey-transparency-dark text-opacity-40 text-sm">
        Select the gallery you want to save this post in
      </h2>
    </div>
  </div>

  <!-- Content -->
  <div
    slot="content"
    class="md:w-[600px] overflow-y-scroll h-full px-4 py-2 border border-blueish-grey-200 rounded-2xl"
    data-simplebar
    data-simplebar-auto-hide="false"
  >
    <div class="flex flex-col gap-2">
      <div class="flex items-center">
        <span class="font-bold text-blueish-grey-900">{galleries.length || 0} Galleries</span>

        <div class="flex-grow" />

        {#if type !== 'talent'}
          <Button
            on:click={openCreateGallery}
            tertiary
            label="Create new"
            sm
            class="!text-brand-green !font-bold"
          >
            <div slot="icon-left">
              <AddSquare class="stroke-brand-green" />
            </div>
          </Button>
        {/if}
      </div>

      {#if error}
        <span class="text-red-500" in:slide|global>
          {error}
        </span>
      {/if}
      {#if success}
        <span class="text-green-500" in:slide|global>
          {success}
        </span>
      {/if}

      {#if galleries.length > 0}
        <hr class="h-0 w-full border-t border-blueish-grey-200 mb-4" in:slide|global />

        <div class="flex flex-col items-center w-full gap-4">
          {#each galleries as gallery, i}
            <GalleryCard
              bind:selected={gallery.selected}
              variant="horizontal"
              selectable
              {gallery}
            />
          {/each}
        </div>
      {:else if isFetchingGalleries}
        <div class="grid place-items-center mt-20" in:fly|global={{ x: 20 }}>
          <Loader size={40} />
        </div>
      {:else}
        <div class="grid place-items-center mt-20" in:fade|global>
          <div class="text-color-gray-4">You do not have any galleries</div>
        </div>
      {/if}
    </div>
  </div>

  <!-- Footer -->
  <div slot="footer">
    <div class="flex items-center gap-2 mt-4">
      {#if nGalleriesSelected}
        <div transition:fade|global class="text-gray-400 flex-grow">
          {nGalleriesSelected} galleries selected
        </div>
      {/if}

      <div class="flex-grow" />

      <div class="flex items-center gap-2">
        <Button secondary clickOnKey="Escape" label="Cancel" on:click={closeModal}>
          <div slot="icon-right">
            <KeyboardHint type="escape" />
          </div>
        </Button>

        <Button
          primary
          clickOnKey="Enter"
          border={false}
          showLoader={$isMobile && isAddingToGalleries}
          iconRight={KeyboardHint}
          label="Add to gallery"
          disabled={nGalleriesSelected < 1 || isFetchingGalleries || isAddingToGalleries}
          on:click={addToGalleries}
        />
      </div>

      {#if !$isMobile && isAddingToGalleries}
        <div in:fly|global={{ x: 20 }}>
          <Loader />
        </div>
      {/if}
    </div>
  </div>
</Modal>

<style lang="postcss">
  :global(.simplebar-scrollbar::before) {
    @apply my-2 translate-x-[5px];
    background: #d0ddd7 !important;
    width: 4px;
  }

  :global(.simplebar-content-wrapper) {
    @apply overscroll-contain;
  }
</style>
